import { cn } from '@/lib/utils';
import type { ViewState } from '@/types';

type Severity = 'crit' | 'warn' | 'info';

export interface Signal {
  id: string;
  severity: Severity;
  tag: string;
  text: string;
  context?: string;
  view?: ViewState;
  hash?: string;
}

interface Props {
  signals: Signal[];
  loading?: boolean;
  onNavigate?: (view: ViewState) => void;
  language?: 'sv' | 'en';
}

const SEV_DOT: Record<Severity, string> = {
  crit: '#C24138',
  warn: '#B97E1F',
  info: '#607C6E',
};

const SEV_TAG: Record<Severity, string> = {
  crit: 'rgba(194,65,56,0.10)',
  warn: 'rgba(185,126,31,0.10)',
  info: 'rgba(96,124,110,0.10)',
};

export function SignalsFeed({ signals, loading, onNavigate, language = 'sv' }: Props) {
  const seeAll = language === 'sv' ? 'Se alla →' : 'View all →';

  return (
    <div className="cmd-panel cmd-panel-inner rounded p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 shrink-0">
        <span className="cmd-label">SIGNALFLÖDE</span>
        <button
          type="button"
          onClick={() => onNavigate?.('notifications')}
          className="cmd-label text-[#8A6A47] hover:text-[#6E5438] transition-colors text-[9px]"
        >
          {seeAll}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto cmd-scroll space-y-1 min-h-0">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-9 rounded animate-pulse"
              style={{ background: 'rgba(7,51,49,0.05)' }}
            />
          ))
        ) : signals.length === 0 ? (
          <div className="flex items-center gap-2 py-3">
            <div
              className="w-1.5 h-1.5 rounded-full shrink-0 cmd-pulse"
              style={{ background: '#1B7F4D' }}
            />
            <span className="text-xs font-mono text-[#1B7F4D]">
              {language === 'sv' ? 'ALLA SYSTEM OK' : 'ALL SYSTEMS OK'}
            </span>
          </div>
        ) : (
          signals.slice(0, 8).map((sig) => (
            <button
              key={sig.id}
              type="button"
              onClick={() => {
                if (sig.hash) {
                  window.location.hash = sig.hash;
                } else if (sig.view && onNavigate) {
                  onNavigate(sig.view);
                }
              }}
              className={cn(
                'w-full text-left flex items-center gap-2.5 px-2 py-2 rounded transition-colors group',
                'hover:bg-[#073331]/5',
              )}
            >
              {/* Severity dot */}
              <div
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: SEV_DOT[sig.severity] }}
              />

              {/* Tag badge */}
              <span
                className="cmd-label shrink-0 px-1.5 py-0.5 rounded text-[9px]"
                style={{
                  background: SEV_TAG[sig.severity],
                  color: SEV_DOT[sig.severity],
                }}
              >
                {sig.tag}
              </span>

              {/* Text */}
              <span className="text-xs font-mono text-[#2E4640] group-hover:text-[#073331] transition-colors flex-1 truncate leading-tight">
                {sig.text}
              </span>

              {/* Context value */}
              {sig.context && (
                <span className="cmd-label text-[#8A6A47] shrink-0">{sig.context}</span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
